import React, { useState, useCallback, useEffect, useRef } from "react";
import { connect } from 'react-redux';
import { Formik } from 'formik';
import { Tabs, Tab } from '@mui/material';

import { Button, TabPanel } from '../../../../components/custom-essentials';
import { SelectSingle, FormikControl, Switch, checkResponses } from '../../../../components/form';
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from '../../../../components/display';
import { CSVExport } from '../../../../components/export';
import StudentTable from './components/StudentTable';
import MemberTable from './components/MemberTable';
import LeadTable from './components/LeadTable';
import { LeadsModal, MemberModal } from '../../../../components/modal';
import { Socket } from '../../../../components/ws';

import {
    fetchContractsIds,
    fetchContractsActiveStudents,
    fetchRpContractsActiveStudents,
    fetchMpContractsActiveStudents,
    fetchMembersAll,
    fetchStudentsAll,
    fetchLeadsAll,
    fetchMpCentersAll,
    fetchRpCentersAll
} from '../../../../actions';
import { getDataArray } from '../../../../components/functions';

const pageTitle = 'Member Accounts';

const userFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'id', label: 'UUID' },
    { value: 'name', label: 'Name' },
    { value: 'mpCenter', label: 'MP Center' },
    { value: 'rpCenter', label: 'RP Center' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'mpPermissions', label: 'MP Permissions' },
    { value: 'rpPermissions', label: 'RP Permissions' },
    { value: 'user_notes', label: 'Notes' },
];

const studentFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'name', label: 'Name' },
    { value: 'mpCenter', label: 'MP Center' },
    { value: 'rpCenter', label: 'RP Center' },
    { value: 'email', label: 'Email' },
];

const leadFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'name', label: 'Name' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'user_notes', label: 'Notes' },
];

function Members(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    // DATA
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    // MODAL
    const [modalMode, setModalMode] = useState(null);

    const {
        fetchMembersAll, fetchStudentsAll, fetchLeadsAll,
        fetchMpCentersAll, fetchRpCentersAll,
        fetchContractsIds, fetchContractsActiveStudents, fetchRpContractsActiveStudents, fetchMpContractsActiveStudents
    } = props;

    const filterData = useCallback((users, students, leads, values) => {
        const { filterType, filterQuery, showMathMembers, showReadingMembers, showInactive } = values;

        const formattedFQ = filterQuery.toLowerCase().trim().replace(/ /g, '');
        function includesText(text) {
            return text.toLowerCase().trim().replace(/ /g, '').includes(formattedFQ);
        }
        const checkId = (u) => {
            if (u.id) return includesText(u.id);
            else if (u.user_id) return includesText(u.user_id);
            else return false;
        }
        const checkName = (u) => {
            const po = u.parentObject || {};
            const sl = u.studentList || [];
            return (
                includesText(`${u.first_name}${u.last_name}`)
                || includesText(`${po.first_name}${po.last_name}`)
                || (sl.some(s => includesText(s)))
            );
        }
        const checkMPCenter = (u) => {
            if (formattedFQ === '') return true;
            if (!u.mpCenterName) return false;
            includesText(u.mpCenterName);
        }
        const checkRPCenter = (u) => {
            if (formattedFQ === '') return true;
            if (!u.rpCenterName) return false;
            includesText(u.rpCenterName);
        }
        const checkEmail = (u) => includesText(u.email);
        const checkPhone = (u) => includesText(u.phone.toString());
        const checkMPPermissions = (u) => {
            if (formattedFQ === '') return true;
            if (!u.mp_permissions) return false;
            includesText(u.mp_permissions);
        }
        const checkRPPermissions = (u) => {
            if (formattedFQ === '') return true;
            if (!u.rp_permissions) return false;
            includesText(u.rp_permissions);
        }
        const checkNotes = (u) => {
            if (formattedFQ === '') return true;
            if (!u.notes) return false;
            includesText(u.notes);
        }

        const showInactiveTrue = showInactive === 'true' || showInactive === true;
        const showMathTrue = showMathMembers === 'true' || showMathMembers === true;
        const showReadingTrue = showReadingMembers === 'true' || showReadingMembers === true;

        const runFilter = (data) => (data.filter(u => {
            switch (filterType.value) {
                case 'all':
                    return checkId(u) || checkName(u) || checkMPCenter(u) || checkRPCenter(u) ||
                        checkEmail(u) || checkPhone(u) || checkMPPermissions(u) || checkRPPermissions(u) || checkNotes(u);
                case 'id':
                    return checkId(u);
                case 'name':
                    return checkName(u);
                case 'mpCenter':
                    return checkMPCenter(u);
                case 'rpCenter':
                    return checkRPCenter(u);
                case 'email':
                    return checkEmail(u);
                case 'phone':
                    return checkPhone(u);
                case 'mpPermissions':
                    return checkMPPermissions(u);
                case 'rpPermissions':
                    return checkRPPermissions(u);
                case 'user_notes':
                    return checkNotes(u);
                default:
                    return false;
            }
        }).filter(u => {
            const isMpUser = (u.mp_permissions && u.mp_permissions?.toLowerCase() !== 'none') || parseInt(u.is_mp_student) === 1;
            const isRpUser = (u.rp_permissions && u.rp_permissions?.toLowerCase() !== 'none') || parseInt(u.is_rp_student) === 1;
            // Check against showInactive filter
            // For students (have user_id), check both mp_active and rp_active
            // For members (no user_id), check mp_active or rp_active
            const isValid1 = showInactiveTrue || parseInt(u.mp_active) === 1 || parseInt(u.rp_active) === 1;
            // Check against showMath and showReading filters
            const isValid2 = showMathTrue && isMpUser;
            const isValid3 = showReadingTrue && isRpUser;
            return isValid1 && (isValid2 || isValid3);
        }));

        const newFilteredUsers = runFilter(users);
        const newFilteredStudents = runFilter(students);
        const newFilteredLeads = runFilter(leads);

        if (mounted.current) {
            setFilteredUsers(newFilteredUsers);
            setFilteredStudents(newFilteredStudents);
            setFilteredLeads(newFilteredLeads);
        }
    }, [setFilteredUsers, setFilteredStudents, mounted]);
    const refreshData = useCallback(() => {
        (async function refresh() {
            if (loading || !formRef.current?.values) return;
            if (mounted.current) setLoading(true);

            try {
                // Detect portal from hostname
                const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
                const isReadingPlex = hostname.includes('readingplex');

                const usersRes = await fetchMembersAll();
                const studentsRes = await fetchStudentsAll();
                const leadsRes = await fetchLeadsAll();
                const mpCentersRes = await fetchMpCentersAll();
                const rpCentersRes = await fetchRpCentersAll();

                // Log response details for debugging
                console.log('[MEMBERS] Response statuses:', {
                    users: usersRes?.status,
                    students: studentsRes?.status,
                    mpCenters: mpCentersRes?.status,
                    rpCenters: rpCentersRes?.status
                });

                // Check response validity - make centers optional based on portal
                // On MathPlex portal, rpCenters is optional (404 is acceptable)
                // On ReadingPlex portal, mpCenters is optional (404 is acceptable)
                const requiredResponses = [usersRes, studentsRes, leadsRes];
                
                if (isReadingPlex) {
                    // ReadingPlex: rpCenters required, mpCenters optional
                    // Only add rpCenters if it has a status (success or failure) - if it fails, we want checkResponses to catch it
                    if (rpCentersRes && rpCentersRes.status !== undefined) {
                        requiredResponses.push(rpCentersRes);
                    }
                    // mpCenters is optional - don't add to requiredResponses, just warn if error
                    if (mpCentersRes && mpCentersRes.status >= 400) {
                        console.warn('[MEMBERS] mpCenters returned error on ReadingPlex portal (optional, continuing):', mpCentersRes.status);
                    }
                } else {
                    // MathPlex: mpCenters required, rpCenters optional
                    // Only add mpCenters if it has a status (success or failure) - if it fails, we want checkResponses to catch it
                    if (mpCentersRes && mpCentersRes.status !== undefined) {
                        requiredResponses.push(mpCentersRes);
                    }
                    // rpCenters is optional - don't add to requiredResponses, just warn if error
                    if (rpCentersRes && (rpCentersRes.status === undefined || rpCentersRes.status >= 400)) {
                        console.warn('[MEMBERS] rpCenters returned error on MathPlex portal (optional, continuing):', rpCentersRes.status || 'empty response');
                    }
                }

                const isApiError = checkResponses(...requiredResponses);
                if (isApiError) {
                    console.error('[MEMBERS] API Error detected:', {
                        users: usersRes,
                        students: studentsRes,
                        mpCenters: mpCentersRes,
                        rpCenters: rpCentersRes
                    });
                    if (mounted.current) {
                        setApiError('Error fetching data from the server. Please try again later.');
                        setLoading(false);
                    }
                    return;
                } else setApiError(false);

                // Use shared getDataArray utility for robust data extraction
                // Handle optional centers gracefully - use empty array if 404
                const newUsers = getDataArray(usersRes);
                const newStudents = getDataArray(studentsRes);
                const newLeads = getDataArray(leadsRes);
                const newMpCenters = (mpCentersRes && mpCentersRes.status < 400) ? getDataArray(mpCentersRes) : [];
                const newRpCenters = (rpCentersRes && rpCentersRes.status < 400) ? getDataArray(rpCentersRes) : [];

                const mpCenterMap = {};
                mpCenterMap[-1] = 'None';
                const rpCenterMap = {};
                rpCenterMap[-1] = 'None';
                if (Array.isArray(newMpCenters)) {
                    newMpCenters.forEach(c => mpCenterMap[parseInt(c.id)] = c.name);
                }
                if (Array.isArray(newRpCenters)) {
                    newRpCenters.forEach(c => rpCenterMap[parseInt(c.id)] = c.name);
                }

                const memberLeadMap = {};
                if (Array.isArray(newUsers)) {
                    newUsers.forEach(u => memberLeadMap[u.id] = u);
                }
                if (Array.isArray(newLeads)) {
                    newLeads.forEach(l => memberLeadMap[l.id] = l);
                }
                if (Array.isArray(newUsers)) {
                    newUsers.forEach(u => {
                        u.mpCenterName = mpCenterMap[u.mp_primary_center] || `Unable to find center (ID :${u.mp_primary_center})`;
                        u.rpCenterName = rpCenterMap[u.rp_primary_center] || `Unable to find center (ID :${u.rp_primary_center})`;
                    });
                }
                if (Array.isArray(newLeads)) {
                    newLeads.forEach(l => {
                        l.mpCenterName = mpCenterMap[l.mp_primary_center] || `Unable to find center (ID :${l.mp_primary_center})`;
                        l.rpCenterName = rpCenterMap[l.rp_primary_center] || `Unable to find center (ID :${l.rp_primary_center})`;
                        l.isLead = true;
                    });
                }

                // Contracts (shared page needs both on BOTH portals)
                // - ReadingPlex: rp_contracts (active contracts), keyed by student UUID
                // - MathPlex: mp_contracts (active contracts), keyed by student UUID
                const rpContractsByStudent = {};
                const mpContractsByStudent = {};

                // Fetch both active sets explicitly (do not depend on hostname)
                // On MathPlex, rpContracts is optional (404 is acceptable)
                // On ReadingPlex, mpContracts is optional (404 is acceptable)
                const [rpActiveRes, mpActiveRes] = await Promise.all([
                    fetchRpContractsActiveStudents(),    // /rp/contracts/students-active
                    fetchMpContractsActiveStudents(),    // /mp/contracts/students-active
                ]);

                // Check contracts - make optional based on portal
                const requiredContractResponses = [];
                if (isReadingPlex) {
                    // ReadingPlex: rpContracts required, mpContracts optional
                    if (rpActiveRes && rpActiveRes.status !== undefined) {
                        requiredContractResponses.push(rpActiveRes);
                    }
                    if (mpActiveRes && mpActiveRes.status >= 400) {
                        console.warn('[MEMBERS] mpContracts returned error on ReadingPlex portal (optional, continuing):', mpActiveRes.status);
                    }
                } else {
                    // MathPlex: mpContracts required, rpContracts optional
                    if (mpActiveRes && mpActiveRes.status !== undefined) {
                        requiredContractResponses.push(mpActiveRes);
                    }
                    if (rpActiveRes && (rpActiveRes.status === undefined || rpActiveRes.status >= 400)) {
                        console.warn('[MEMBERS] rpContracts returned error on MathPlex portal (optional, continuing):', rpActiveRes.status || 'empty response');
                    }
                }

                const isApiError2 = checkResponses(...requiredContractResponses);
                if (isApiError2) {
                    if (mounted.current) {
                        setApiError('Error fetching contracts from the server. Please try again later.');
                        setLoading(false);
                    }
                    return;
                } else setApiError(false);

                // Handle optional contracts gracefully - use empty array if 404
                const rpContracts = (rpActiveRes && rpActiveRes.status < 400) ? getDataArray(rpActiveRes) : [];
                const mpContracts = (mpActiveRes && mpActiveRes.status < 400) ? getDataArray(mpActiveRes) : [];

                if (Array.isArray(rpContracts)) {
                    rpContracts.forEach(c => {
                        const sid = c.student;
                        if (!sid) return;
                        if (!rpContractsByStudent[sid]) rpContractsByStudent[sid] = c;
                    });
                }
                if (Array.isArray(mpContracts)) {
                    mpContracts.forEach(c => {
                        const sid = c.student;
                        if (!sid) return;
                        if (!mpContractsByStudent[sid]) mpContractsByStudent[sid] = c;
                    });
                }

                const studentParentMap = {};
                if (Array.isArray(newStudents)) {
                    newStudents.forEach(s => {
                        const relUser = memberLeadMap[s.user_id] || {};
                        s.id = s.user_id;
                        s.email = relUser.email || '';
                        s.phone = relUser.phone || '';
                        s.mp_permissions = (relUser.mp_permissions && typeof relUser.mp_permissions === 'string') ? relUser.mp_permissions.toLowerCase() : 'none';
                        s.rp_permissions = (relUser.rp_permissions && typeof relUser.rp_permissions === 'string') ? relUser.rp_permissions.toLowerCase() : 'none';
                        // Contracts (always attach both; columns can choose what to show per portal)
                        s.rpContract = rpContractsByStudent[s.user_id] || {};
                        s.mpContract = mpContractsByStudent[s.user_id] || {};
                        s.userObject = memberLeadMap[s.user_id] || {};
                        const parent = memberLeadMap[s.parent];
                        s.parentObject = parent || {};
                        s.mpCenterName = mpCenterMap[s.mp_primary_center] || `Unable to find center (ID :${s.mp_primary_center})`;
                        s.rpCenterName = rpCenterMap[s.rp_primary_center] || `Unable to find center (ID :${s.rp_primary_center})`;

                        studentParentMap[s.user_id] = parent;

                        if (parent) {
                            if (!parent.studentList) parent.studentList = [];
                            parent.studentList.push(`${s.first_name} ${s.last_name}`);
                        }
                    });
                }

                if (Array.isArray(newUsers)) {
                    newUsers.forEach(u => {
                        u.parentObject = studentParentMap[u.id] || {};
                    });
                }
                if (Array.isArray(newLeads)) {
                    newLeads.forEach(l => {
                        l.parentObject = studentParentMap[l.id] || {};
                    });
                }

                if (mounted.current) {
                    setUsers(newUsers);
                    setStudents(newStudents);
                    setLeads(newLeads);
                    filterData(newUsers, newStudents, newLeads, formRef.current.values);
                    setLoading(false);
                }
            } catch (error) {
                console.error('[MEMBERS] Unexpected error:', error);
                if (mounted.current) {
                    setApiError('An unexpected error occurred. Please try again later.');
                    setLoading(false);
                }
            }
        })();
    }, [loading, formRef, mounted, fetchMpCentersAll, fetchRpCentersAll, fetchMembersAll, fetchStudentsAll,
        fetchLeadsAll, fetchContractsIds, setUsers, setStudents, filterData, setLoading]);
    useEffect(() => {

        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // For the CREATE button
    function handleShowModal(mode) {
        setModalMode(mode);
    }
    function onSubmitCallback(changed = true) {
        if (changed) refreshData();
        setModalMode(null);
    }

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {/* CREATE mode only (edit/delete handled in table) */}
            {modalMode === 'member' &&
                <MemberModal
                    mode="create"
                    onSubmitCallback={onSubmitCallback}
                />
            }
            {modalMode === 'lead' &&
                <LeadsModal
                    mode="create"
                    onSubmitCallback={onSubmitCallback}
                />
            }
            <div className="card">
                <h2>Member Accounts</h2>
                <br />
                <Formik
                    enableReinitialize
                    initialValues={{
                        tabActiveKey: 0,
                        filterType: { value: 'all', label: 'All' },
                        filterQuery: '',
                        // Default to ReadingPlex on ReadingPlex domain, MathPlex on MathPlex domain
                        showMathMembers: typeof window !== 'undefined' && !window.location.hostname.includes('readingplex'),
                        showReadingMembers: typeof window !== 'undefined' && window.location.hostname.includes('readingplex'),
                        showInactive: false,
                    }}
                    innerRef={formRef}
                    handleSubmit={() => null}
                >
                    {formik => (
                        <form onSubmit={formik.handleSubmit}>
                            <div className="flex flex-row gap-x-4 items-center">
                                <div className="grid grid-cols-1 gap-y-2 w-1/4">
                                    <SelectSingle
                                        id="members-type-1"
                                        value={formik.values.filterType}
                                        name="filterType"
                                        label="Filter Type"
                                        onChange={(e) => {
                                            const newValues = { ...formik.values };
                                            newValues.filterType = e.target.value;
                                            formik.setValues(newValues);
                                            filterData(users, students, leads, newValues);
                                        }}
                                        options={formik.values.tabActiveKey === 0 ? userFilterOptions : studentFilterOptions}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-2 w-1/4">
                                    <FormikControl
                                        id="members-query-1"
                                        name="filterQuery"
                                        placeholder="Enter a filter query..."
                                        value={formik.values.filterQuery}
                                        onChange={(e) => {
                                            const newValues = { ...formik.values };
                                            newValues.filterQuery = e.target.value;
                                            formik.setValues(newValues);
                                            filterData(users, students, leads, newValues);
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-2">
                                    <Switch
                                        name="showInactive"
                                        label="Show Inactive"
                                        color="mpLRed"
                                        checked={formik.values.showInactive}
                                        onChange={(e) => {
                                            const newValues = { ...formik.values };
                                            newValues.showInactive = e.target.value;
                                            formik.setValues(newValues);
                                            filterData(users, students, leads, newValues);
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-2">
                                    <Switch
                                        name="showMathMembers"
                                        label="Show MP"
                                        color="mpLRed"
                                        checked={formik.values.showMathMembers}
                                        onChange={(e) => {
                                            const newValues = { ...formik.values };
                                            newValues.showMathMembers = e.target.value;
                                            formik.setValues(newValues);
                                            filterData(users, students, leads, newValues);
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-2">
                                    <Switch
                                        name="showReadingMembers"
                                        label="Show RP"
                                        color="mpLRed"
                                        checked={formik.values.showReadingMembers}
                                        onChange={(e) => {
                                            const newValues = { ...formik.values };
                                            newValues.showReadingMembers = e.target.value;
                                            formik.setValues(newValues);
                                            filterData(users, students, leads, newValues);
                                        }}
                                    />
                                </div>
                            </div>

                            <br />

                            <div className="flex flex-row gap-x-4 items-center">
                                <div className="grid grid-cols-1 gap-y-2">
                                    <Button
                                        onClick={() => handleShowModal('member')}
                                        color="lte-mpLBlue"
                                    >
                                        + New User
                                    </Button>
                                </div>
                                <div className="grid grid-cols-1 gap-y-2">
                                    <Button
                                        onClick={() => handleShowModal('lead')}
                                        color="lte-mpLBlue"
                                    >
                                        + New Lead
                                    </Button>
                                </div>
                                <div className="grid grid-cols-1 gap-y-2 ml-auto">
                                    <TooltipWrapper
                                        tooltipText={
                                            <div>
                                                <div>
                                                    What gets exported?
                                                </div>
                                                <br />
                                                <div>
                                                    All users that are currently filtered
                                                    ({formik.values.tabActiveKey === 0 ?
                                                        filteredUsers.length : filteredStudents.length} items).
                                                    This depends on the tab that is selected.
                                                </div>
                                            </div>
                                        }
                                    >
                                        <CSVExport
                                            title="Members"
                                            label="Export users to CSV"
                                            data={formik.values.tabActiveKey === 0 ? filteredUsers : filteredStudents}
                                        />
                                    </TooltipWrapper>
                                </div>
                            </div>

                            <div className="h-8 clear-both" />

                            {apiError ? <div className="text-mpLRed">{apiError}</div>
                                :
                                <>
                                    <Tabs
                                        id="members"
                                        value={formik.values.tabActiveKey}
                                        onChange={(e, newValue) => {
                                            // Set the key
                                            const key = parseInt(newValue);
                                            const newValues = { ...formik.values };
                                            newValues.tabActiveKey = key;

                                            // Update the selected type option if the currently selected option
                                            // is not included in the new options
                                            // 0 = users, 1 = students, 2 = leads
                                            const typeOptions = key === 0 ? userFilterOptions :
                                                key === 1 ? studentFilterOptions :
                                                    key === 2 ? leadFilterOptions : [{ value: -1, label: 'Error loading tab...' }];
                                            const currentType = formik.values.filterType.value
                                            newValues.filterType = typeOptions.find(o => o.value === currentType) || { value: 'all', label: 'All' };
                                            formik.setValues(newValues);

                                            // Refresh data with updated filterType, if a change was made
                                            if (currentType !== newValues.filterType.value) {
                                                filterData(users, students, leads, newValues.filterType,
                                                    formik.values.filterQuery, formik.values.showInactive)
                                            }
                                        }}
                                    >
                                        <Tab label={`Users (${filteredUsers.length})`} />
                                        <Tab label={`Students (${filteredStudents.length})`} />
                                        <Tab label={`Leads (${filteredLeads.length})`} />
                                    </Tabs>
                                    <TabPanel activeKey={formik.values.tabActiveKey} index={0}>
                                        <MemberTable
                                            refreshData={refreshData}
                                            memberData={filteredUsers}
                                        />
                                    </TabPanel>
                                    <TabPanel activeKey={formik.values.tabActiveKey} index={1}>
                                        <StudentTable
                                            refreshData={refreshData}
                                            studentData={filteredStudents}
                                        />
                                    </TabPanel>
                                    <TabPanel activeKey={formik.values.tabActiveKey} index={2}>
                                        <LeadTable
                                            refreshData={refreshData}
                                            leadData={filteredLeads}
                                        />
                                    </TabPanel>
                                </>
                            }

                        </form>
                    )}
                </Formik>
            </div>

            <Socket
                refreshData={refreshData}
                page={pageTitle}
                setVersion={props.setVersion}
            />

            {loading && <LoadingOverlay />}
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    };
}

export default connect(mapStateToProps, {
    fetchContractsIds,
    fetchContractsActiveStudents,
    fetchRpContractsActiveStudents,
    fetchMpContractsActiveStudents,
    fetchMembersAll,
    fetchStudentsAll,
    fetchLeadsAll,
    fetchMpCentersAll,
    fetchRpCentersAll
})(Members);